import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
const ClaudeChat = React.lazy(() => import('./pages/ClaudeChat'));
const ComputerControl = React.lazy(() => import('./pages/ComputerControl'));
const AndroidControl = React.lazy(() => import('./pages/AndroidControl'));
const QRScanner = React.lazy(() => import('./pages/QRScanner'));
const Settings = React.lazy(() => import('./pages/Settings'));
const PromptGenerator = React.lazy(() => import('./pages/PromptGenerator'));
const SmartScan = React.lazy(() => import('./pages/SmartScan'));

const OrchestrationFarm = React.lazy(() => import('./pages/OrchestrationFarm'));
const MultiAgentDesign = React.lazy(() => import('./pages/MultiAgentDesign'));
const AgentRegistry = React.lazy(() => import('./pages/AgentRegistry'));
const MobileExtensions = React.lazy(() => import('./pages/MobileExtensions'));
const EligibilityChecker = React.lazy(() => import('./pages/EligibilityChecker'));
const WallesterDashboard = React.lazy(() => import('./pages/WallesterDashboard'));
const AdminPayouts = React.lazy(() => import('./pages/AdminPayouts'));
const CredentialVault = React.lazy(() => import('./pages/CredentialVault'));
const SignupFlow = React.lazy(() => import('./pages/SignupFlow'));
const VerificationPipeline = React.lazy(() => import('./pages/VerificationPipeline'));
const OwnerDetail = React.lazy(() => import('./pages/OwnerDetail'));
const AlternativeOffers = React.lazy(() => import('./pages/AlternativeOffers'));
const SystemConfig = React.lazy(() => import('./pages/SystemConfig'));
const ActionLog = React.lazy(() => import('./pages/ActionLog'));
const PartnerManagement = React.lazy(() => import('./pages/PartnerManagement'));
const TelegramExtractor = React.lazy(() => import('./pages/TelegramExtractor'));
const PlatformCatalog = React.lazy(() => import('./pages/PlatformCatalog'));
const CurrencyConverter = React.lazy(() => import('./pages/CurrencyConverter'));
const SecurityDashboard = React.lazy(() => import('./pages/SecurityDashboard'));
const AICreatives = React.lazy(() => import('./pages/AICreatives'));
const SocialAutomation = React.lazy(() => import('./pages/SocialAutomation'));
const NoteAnalyzer = React.lazy(() => import('./pages/NoteAnalyzer'));
const WallesterAPI = React.lazy(() => import('./pages/WallesterAPI'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const PersonalDev = React.lazy(() => import('./pages/PersonalDev'));
const ProjectChangelog = React.lazy(() => import('./pages/ProjectChangelog'));
const FileDashboard = React.lazy(() => import('./pages/FileDashboard'));
const OCDashboard = React.lazy(() => import('./pages/OCDashboard'));
const OCCalendar = React.lazy(() => import('./pages/OCCalendar'));
const OCResources = React.lazy(() => import('./pages/OCResources'));
const OCWatchtower = React.lazy(() => import('./pages/OCWatchtower'));
const OCPlayground = React.lazy(() => import('./pages/OCPlayground'));
const ExternalProject = React.lazy(() => import('./pages/ExternalProject'));
import OpenBalancerDashboard from './pages/OpenBalancerDashboard';
const FinansProtectHub = React.lazy(() => import('./pages/FinansProtectHub'));
const AutomationDashboard = React.lazy(() => import('./pages/automation/AutomationDashboard'));
const SessionManager = React.lazy(() => import('./pages/automation/SessionManager'));
const SandboxPlayground = React.lazy(() => import('./pages/automation/SandboxPlayground'));
const PageAnalyzer = React.lazy(() => import('./pages/automation/PageAnalyzer'));
const MonitorCenter = React.lazy(() => import('./pages/automation/MonitorCenter'));
const TaskBuilder = React.lazy(() => import('./pages/automation/TaskBuilder'));
const HermesMonitoring = React.lazy(() => import('./pages/HermesMonitoring'));
const TelegramHubControl = React.lazy(() => import('./pages/TelegramHubControl'));
const ObsidianMemories = React.lazy(() => import('./pages/ObsidianMemories'));
const ContainerVaultDashboard = React.lazy(() => import('./pages/ContainerVaultDashboard'));
const HermesHubView = React.lazy(() => import('./pages/HermesHubView'));
const GitHubEcosystemVault = React.lazy(() => import('./pages/GitHubEcosystemVault'));
const FleetResourceBalancer = React.lazy(() => import('./pages/FleetResourceBalancer'));
const TailnetDevices = React.lazy(() => import('./pages/TailnetDevices'));

const KimiK3Control = React.lazy(() => import('./pages/KimiK3Control'));
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider } from './context/ThemeContext';
import { useAuth } from './context/AuthContext';
import Login from './components/Login';
import { Toaster } from 'sonner';

import { initA11yKeyboardShortcuts } from './utils/a11yController';

function App() {
  const { user } = useAuth();
  const getInitialPage = () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const pageParam = params.get('page');
      if (pageParam) return pageParam;
      if (window.location.pathname.startsWith('/openbalancer')) return 'openbalancer';
    }
    return 'openbalancer';
  };
  const [activePage, setActivePage] = useState(getInitialPage());
  const [sidebarOpen, setSidebarOpen] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);

  useEffect(() => {
    const cleanupShortcuts = initA11yKeyboardShortcuts();
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (cleanupShortcuts) cleanupShortcuts();
    };
  }, []);

  // Public route: /check is accessible without authentication
  // This is the entry point of the Wallestars registration funnel
  if (window.location.pathname === '/check') {
    return (
      <ThemeProvider>
        <EligibilityChecker />
        <Toaster theme="dark" position="bottom-right" />
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider>
        <Login />
        <Toaster theme="dark" position="bottom-right" />
      </ThemeProvider>
    );
  }

  const pages = {
    dashboard: <Dashboard />,
    chat: <ClaudeChat />,
    computer: <ComputerControl />,
    android: <AndroidControl />,
    qrscanner: <QRScanner />,
    smartscan: <SmartScan />,
    promptgen: <PromptGenerator />,
    hostinger: <OpenBalancerDashboard />,
    orchestration: <OrchestrationFarm />,
    multiagent: <MultiAgentDesign />,
    agentregistry: <AgentRegistry />,
    extensions: <MobileExtensions />,
    eligibility: <EligibilityChecker />,
    wallesterdash: <WallesterDashboard />,
    adminpayouts: <AdminPayouts />,
    credentialvault: <CredentialVault />,
    signupflow: <SignupFlow />,
    verification: <VerificationPipeline />,
    ownerdetail: (
      <OwnerDetail
        ownerId={selectedOwnerId}
        onBack={() => setActivePage('wallesterdash')}
      />
    ),
    alternatives: <AlternativeOffers />,
    systemconfig: <SystemConfig />,
    actionlog: <ActionLog />,
    partners: <PartnerManagement />,
    telegramextractor: <TelegramExtractor />,
    platformcatalog: <PlatformCatalog />,
    currency: <CurrencyConverter />,
    security: <SecurityDashboard />,
    aicreatives: <AICreatives />,
    socialautomation: <SocialAutomation />,
    noteanalyzer: <NoteAnalyzer />,
    wallesterapi: <WallesterAPI />,
    landing: <LandingPage />,
    personaldev: <PersonalDev />,
    changelog: <ProjectChangelog />,
    filedashboard: <FileDashboard />,
    'oc-dashboard': <OCDashboard />,
    'oc-calendar': <OCCalendar />,
    'oc-resources': <OCResources />,
    'oc-watchtower': <OCWatchtower />,
    'oc-playground': <OCPlayground />,
    openbalancer: <OpenBalancerDashboard />,
    hermesgateways: <HermesMonitoring />,
    telegramhub: <TelegramHubControl />,
    obsidianmemories: <ObsidianMemories />,
    containervault: <ContainerVaultDashboard />,
    kimik3: <KimiK3Control />,
    hermeshub: <HermesHubView />,
    githubecosystem: <GitHubEcosystemVault />,
    fleetbalancer: <FleetResourceBalancer />,
    tailnetdevices: <TailnetDevices />,
    'hostinger-oauth': <OpenBalancerDashboard />,
    finansprotect: <FinansProtectHub />,
    'ext-hostinger-dashboard': <OpenBalancerDashboard />,
    'ext-openclaw-hub': <HermesHubView />,
    'ext-file-dashboard': <ExternalProject url="https://infisical.openbalancer.com/" title="Infisical Vault" />,
    'ext-infisical': <ExternalProject url="https://infisical.openbalancer.com/" title="Infisical Vault" />,
    // Browser Automation
    'automation-dashboard': <AutomationDashboard />,
    'automation-sessions': <SessionManager />,
    'automation-sandbox': <SandboxPlayground />,
    'automation-analyzer': <PageAnalyzer />,
    'automation-monitor': <MonitorCenter />,
    'automation-taskbuilder': <TaskBuilder />,
    settings: <Settings />
  };

  return (
    <ThemeProvider>
      <SocketProvider>
        <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 animate-gradient">
          {/* Background effects */}
          <div className="fixed inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 -left-4 w-96 h-96 bg-primary-500/10 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-slow"></div>
            <div className="absolute top-0 -right-4 w-96 h-96 bg-primary-600/10 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-slow animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-96 h-96 bg-primary-400/10 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-slow animation-delay-4000"></div>
          </div>

          <div className="relative flex">
            {/* Sidebar */}
            <Sidebar
              activePage={activePage}
              setActivePage={setActivePage}
              isOpen={sidebarOpen}
              setIsOpen={setSidebarOpen}
            />

            {/* Main content */}
            <div className={`flex-1 transition-all duration-300 min-w-0 ${sidebarOpen ? 'ml-0 lg:ml-64' : 'ml-0 lg:ml-20'}`}>
              <Header
                toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                sidebarOpen={sidebarOpen}
              />

              <main className="p-3 sm:p-6 min-w-0 overflow-x-hidden">
                {pages[activePage]}
              </main>
            </div>
          </div>
        </div>
        <Toaster theme="dark" position="bottom-right" />
      </SocketProvider>
    </ThemeProvider>
  );
}

export default App;
